import { useEffect, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useScroll } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export const Preloader = () => {
  const [done, setDone] = useState(() => sessionStorage.getItem("waghe_intro") === "1");
  useEffect(() => {
    if (done) return;
    const t = setTimeout(() => {
      sessionStorage.setItem("waghe_intro", "1");
      setDone(true);
    }, 1700);
    return () => clearTimeout(t);
  }, [done]);
  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          data-testid="site-preloader"
          className="fixed inset-0 z-[100] bg-ink flex flex-col items-center justify-center"
          exit={{ opacity: 0, transition: { duration: 0.6, ease: "easeInOut" } }}
        >
          <motion.div
            initial={{ scale: 0.75, opacity: 0, rotate: -8 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="w-20 h-20 rounded-full border border-gold/60 flex items-center justify-center bg-coal"
          >
            <span className="font-display font-bold text-2xl text-gold">WS</span>
          </motion.div>
          <span className="block overflow-hidden mt-6">
            <motion.span
              initial={{ y: "112%" }}
              animate={{ y: "0%" }}
              transition={{ delay: 0.25, duration: 0.7, ease: EASE }}
              className="block font-display text-2xl sm:text-3xl text-cream tracking-tight"
            >
              Waghe <span className="italic text-gold">&amp;</span> Sons
            </motion.span>
          </span>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-[10px] uppercase tracking-[0.42em] text-zinc-500 mt-3"
          >
            Food Pvt. Ltd. · Est. 1965
          </motion.p>
          <div className="w-44 h-px bg-white/10 mt-8 overflow-hidden">
            <motion.div
              className="h-full bg-gold"
              initial={{ x: "-100%" }}
              animate={{ x: "0%" }}
              transition={{ duration: 1.15, ease: "easeInOut", delay: 0.15 }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const CursorGlow = () => {
  const [enabled, setEnabled] = useState(false);
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const rx = useSpring(x, { stiffness: 260, damping: 28, mass: 0.6 });
  const ry = useSpring(y, { stiffness: 260, damping: 28, mass: 0.6 });

  useEffect(() => {
    if (!window.matchMedia("(pointer: fine)").matches) return;
    setEnabled(true);
    const move = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
    };
    window.addEventListener("mousemove", move, { passive: true });
    return () => window.removeEventListener("mousemove", move);
  }, [x, y]);

  if (!enabled) return null;
  return (
    <motion.div aria-hidden className="fixed top-0 left-0 z-[94] pointer-events-none" style={{ x: rx, y: ry }}>
      <div className="-translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full border border-gold/45" />
      <div className="absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-gold" />
    </motion.div>
  );
};

export const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.4 });
  return (
    <motion.div
      data-testid="scroll-progress"
      aria-hidden
      className="fixed top-0 left-0 right-0 h-[3px] z-[96] origin-left bg-gradient-to-r from-gold-dark via-gold to-gold-light"
      style={{ scaleX }}
    />
  );
};
